import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { 
  Search, Music, Tv, Mic, Volume2, QrCode, 
  ChevronDown, ChevronUp, ArrowRight, Sparkles 
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Chatbot from '../components/common/Chatbot';
import GlowButton from '../components/ui/GlowButton';
import './Landing.css';

export default function Landing() {
  const { t } = useTranslation();
  const { user, hasActivePlan } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [openFaq, setOpenFaq] = useState(null);
  const revealRefs = useRef([]);

  // Si se navegó con hash (ej: /#features o /#faq desde otra página), scrollear suavemente
  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace('#', '');
      const el = document.getElementById(targetId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.hash]);

  // Efecto Scroll Reveal estilo Fotoevento con IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleStartFree = () => {
    if (!user) {
      navigate('/register');
    } else if (!hasActivePlan()) {
      navigate('/pricing');
    } else {
      navigate('/dashboard');
    }
  };

  const handleViewPlans = () => {
    navigate('/pricing');
  };

  const features = [
    { key: 'Search', icon: <Search className="w-6 h-6 text-pink-400" />, titleKey: 'landing.featureSearch', descKey: 'landing.featureSearchDesc' },
    { key: 'Playlist', icon: <Music className="w-6 h-6 text-purple-400" />, titleKey: 'landing.featurePlaylist', descKey: 'landing.featurePlaylistDesc' },
    { key: 'Screen', icon: <Tv className="w-6 h-6 text-blue-400" />, titleKey: 'landing.featureScreen', descKey: 'landing.featureScreenDesc' },
    { key: 'Interactive', icon: <Mic className="w-6 h-6 text-emerald-400" />, titleKey: 'landing.featureInteractive', descKey: 'landing.featureInteractiveDesc' },
    { key: 'Effects', icon: <Volume2 className="w-6 h-6 text-amber-400" />, titleKey: 'landing.featureEffects', descKey: 'landing.featureEffectsDesc' },
    { key: 'QR', icon: <QrCode className="w-6 h-6 text-cyan-400" />, titleKey: 'landing.featureQR', descKey: 'landing.featureQRDesc' },
  ];

  const faqs = [
    { qKey: 'landing.faq1Q', aKey: 'landing.faq1A' },
    { qKey: 'landing.faq2Q', aKey: 'landing.faq2A' },
    { qKey: 'landing.faq3Q', aKey: 'landing.faq3A' },
    { qKey: 'landing.faq4Q', aKey: 'landing.faq4A' },
    { qKey: 'landing.faq5Q', aKey: 'landing.faq5A' },
    { qKey: 'landing.faq6Q', aKey: 'landing.faq6A' },
  ];

  // Texto del botón dinámico según sesión
  const startButtonText = !user
    ? t('landing.ctaStart', 'Comenzar Gratis')
    : !hasActivePlan()
      ? t('landing.ctaChoosePlan', 'Elegir Plan')
      : t('landing.ctaGoDashboard', 'Ir al Karaoke');

  return (
    <div className="landing" id="landing-page">
      {/* Navbar con versión v2.4.0 */}
      <Navbar />

      {/* Hero Section Cinematográfico */}
      <section className="landing__hero" id="hero-section">
        <div className="landing__hero-bg"></div>
        <div className="landing__hero-overlay"></div>

        {/* Orbes luminosos animados */}
        <div className="landing__hero-glow landing__hero-glow--1"></div>
        <div className="landing__hero-glow landing__hero-glow--2"></div>
        <div className="landing__hero-glow landing__hero-glow--3"></div>

        <div className="landing__hero-content animate-fade-in">
          {/* Badge estilo Fotoevento */}
          <div className="landing__badge">
            <Sparkles className="w-4 h-4 text-pink-400 inline mr-1" />
            <span>{t('landing.badge', 'Karaoke en Vivo · YouTube · Interactividad Móvil')}</span>
          </div>

          <h1 className="landing__hero-title">
            <span className="block text-white">{t('landing.heroTitle1', 'Tu Fiesta de Karaoke,')}</span>{' '}
            <span className="block text-gradient">{t('landing.heroTitle2', 'al Siguiente Nivel')}</span>
          </h1>

          <p className="landing__hero-subtitle">
            {t('landing.heroSubtitle', 'Buscá pistas en YouTube, organizá playlists con nombres de cantantes, proyectá en pantalla completa y hacé que tus invitados participen en vivo desde sus celulares.')}
          </p>

          <div className="landing__hero-cta">
            <GlowButton variant="primary" size="lg" onClick={handleStartFree} id="cta-start-free">
              {startButtonText}
            </GlowButton>
            <GlowButton variant="ghost" size="lg" onClick={handleViewPlans} id="cta-view-plans">
              {t('landing.ctaViewPlans', 'Ver Planes')}
            </GlowButton>
          </div>
        </div>
      </section>

      {/* Sección de Características (#features) con Scroll Reveal */}
      <section className="landing__features reveal-on-scroll" id="features">
        <div className="landing__features-header">
          <h2 className="landing__features-title text-gradient">
            {t('landing.featuresTitle', 'Todo lo que necesitás para tu karaoke')}
          </h2>
          <p className="landing__features-subtitle">
            {t('landing.featuresSubtitle', 'Diseñado tanto para juntadas con amigos como para bares, salones y eventos profesionales.')}
          </p>
        </div>

        <div className="landing__features-grid">
          {features.map((f, i) => (
            <div
              key={f.key}
              className={`landing__feature-card glass-panel reveal-on-scroll reveal-delay-${(i % 3) + 1}`}
            >
              <div className="landing__feature-icon-box">
                {f.icon}
              </div>
              <h3 className="landing__feature-title">{t(f.titleKey)}</h3>
              <p className="landing__feature-desc">{t(f.descKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Sección de Preguntas Frecuentes (#faq) con Scroll Reveal */}
      <section className="landing__faq reveal-on-scroll" id="faq">
        <div className="landing__faq-container">
          <div className="landing__faq-header">
            <h2 className="landing__faq-title text-gradient">
              {t('landing.faqTitle', 'Preguntas Frecuentes')}
            </h2>
            <p className="landing__faq-subtitle">
              {t('landing.faqSubtitle', 'Respuestas a las dudas más comunes para poner en marcha tu karaoke en minutos.')}
            </p>
          </div>

          <div className="landing__faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div 
                  key={index} 
                  className={`landing__faq-item ${isOpen ? 'landing__faq-item--open' : ''}`}
                >
                  <button
                    type="button"
                    className="landing__faq-question"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    id={`faq-question-${index}`}
                  >
                    <span className="landing__faq-question-text">{t(faq.qKey)}</span>
                    <span className="landing__faq-icon">
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5 text-pink-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-white/50 flex-shrink-0" />
                      )}
                    </span>
                  </button>
                  <div
                    className="landing__faq-collapse"
                    style={{
                      maxHeight: isOpen ? '400px' : '0px',
                      opacity: isOpen ? 1 : 0,
                      transition: 'max-height 0.35s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease',
                      overflow: 'hidden',
                    }}
                  >
                    <div className="landing__faq-answer">
                      <p>{t(faq.aKey)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sección "¿Listo para tu próximo karaoke?" con Scroll Reveal */}
      <section className="landing__cta-section reveal-on-scroll">
        <div className="landing__cta-card">
          <div className="landing__cta-glow"></div>
          <div className="landing__cta-content">
            <h2 className="landing__cta-title">
              {t('landing.ctaReadyTitle', '¿Listo para tu próximo karaoke?')}
            </h2>
            <p className="landing__cta-subtitle">
              {t('landing.ctaReadySubtitle', 'Creá tu sala en minutos, conectá tu pantalla y sorprendé a todos tus invitados con una experiencia inolvidable.')}
            </p>
            <div className="landing__cta-action">
              <GlowButton variant="primary" size="lg" onClick={handleStartFree}>
                <span>{t('landing.ctaReadyBtn', 'Empezar Ahora')}</span>
                <ArrowRight className="w-5 h-5" />
              </GlowButton>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Pro */}
      <Footer />

      {/* Burbuja Flotante de Asistente de Chat */}
      <Chatbot />
    </div>
  );
}
